import axios from 'axios'
import dayjs from 'dayjs'

import * as NetworkErrorDialog from 'slices/networkErrorDialogSlice'
import * as Spinner from 'slices/spinnerSlice'

import type { Action, ActionCreatorWithPayload, ThunkDispatch } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import type { RootState } from 'store'

const UNAUTHORIZED = '401'
const UNREACHABLE = '999'
export const ERROR_STATUS_CODE = {
  BAD_REQUEST: '400',
  UNAUTHORIZED,
  FORBIDDEN: '403',
  NOT_FOUND: '404',
  CONFLICT: '409',
  UNREACHABLE,
  UNKNOWN: '000',
}
export const ENABLE_DIALOG_ERROR_STATUS_CODES = [UNAUTHORIZED, UNREACHABLE]
export const INCORRECT_OLD_PASSWORD_MESSAGE = 'Incorrect oldPassword.'

export const BAD_REQUEST_CODE = {
  BAD_REQUEST: 'Bad Request',
} as const
export const UNAUTHORIZED_CODE = {
  UNAUTHORIZED: 'Unauthorized',
} as const
export const FORBIDDEN_CODE = {
  FORBIDDEN: 'Forbidden',
  IP_ADDRESS_RESTRICTION: 'IP Address Restriction',
} as const
export const NOT_FOUND_CODE = {
  NOT_FOUND: 'Not Found',
} as const
export const CONFLICT_CODE = {
  CONFLICT: 'Conflict',
} as const
export const INTERNAL_SERVER_ERROR_CODE = {
  INTERNAL_SERVER_ERROR: 'Internal Server Error',
} as const

export type CommonParams = {
  accessToken: string | null
  idToken: string | null
  tenantId: number
  userId: string
  updatedBy: string
}

export type ApiErrorResponse = {
  status: string
  data: ApiErrorResponseData
}

type ApiErrorResponseData = {
  code?: string
  message?: string | string[]
  data?: object
}

export const commonParams = (getState: () => RootState): CommonParams => {
  const {
    accessToken,
    idToken,
    user: {
      userId,
      userHasTenants: {
        0: { nickname, id },
      },
    },
    team: { workerName },
  } = getState().session
  const tenantId = id || 0
  return {
    accessToken,
    idToken,
    tenantId,
    userId,
    updatedBy: workerName || (nickname ?? ''),
  }
}

const SPLIT_DAYS = 10
export const getSplitPeriods = (from: string, to: string): [string, string][] => {
  const totalDays = dayjs(to).diff(from, 'days') + 1
  const periods: [string, string][] = []
  // APIのパフォーマンス対策でSPLIT_DAYS日ごとに期間を分割して取得する
  let currentFrom = 0
  while (currentFrom < totalDays) {
    const currentTo = Math.min(currentFrom + SPLIT_DAYS - 1, totalDays - 1)
    periods.push([
      dayjs(from).add(currentFrom, 'day').format('YYYY-MM-DD'),
      dayjs(from).add(currentTo, 'day').format('YYYY-MM-DD'),
    ])
    currentFrom += SPLIT_DAYS
  }
  return periods
}

export const checkStatus = async (
  callback: () => Promise<number | undefined>,
  dispatch: ThunkDispatch<RootState, unknown, Action<string>>
) => {
  const retryInterval = await callback()
  if (!retryInterval) {
    return
  }
  dispatch(Spinner.start())
  await new Promise<void>(resolve => setTimeout(resolve, retryInterval))
  dispatch(Spinner.stop())
  await checkStatus(callback, dispatch)
}

export const makeErrorResponse = (res: AxiosError): ApiErrorResponse => {
  return {
    status: (res.response?.status || UNREACHABLE).toString(),
    data: res.response?.data as ApiErrorResponseData,
  }
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message
    if (typeof message === 'string') {
      return message
    }
  }
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

export const handleApiError = (
  res: unknown,
  dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
  apiFailure: ActionCreatorWithPayload<{ errorMessage: string }, string>
): void => {
  if (axios.isAxiosError(res)) {
    const errorCode = makeErrorResponse(res).status
    dispatch(apiFailure({ errorMessage: errorCode }))
    return
  }
  const errorMessage = getErrorMessage(res)
  dispatch(apiFailure({ errorMessage: ERROR_STATUS_CODE.UNKNOWN }))
  dispatch(NetworkErrorDialog.open({ code: ERROR_STATUS_CODE.UNKNOWN, errorMessage: errorMessage }))
}

export const downloadByURL = async (url: string, filename: string) => {
  const response = await fetch(url)
  const blob = await response.blob()
  exportData(blob, filename)
}

export const exportData = (blob: Blob, filename: string) => {
  const objUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.download = filename
  link.href = objUrl
  link.click()
  window.URL.revokeObjectURL(objUrl)
}

export const putUploadUrl = async (url: string, csvContent: ArrayBuffer): Promise<void> => {
  const headers = {
    'Content-Type': 'text/csv',
  }
  await axios.put(url, csvContent, { headers })
}
